import React from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import { Box, Stack, Typography } from '@mui/material';

import {
  hasIssueDetail,
  HOVER_DETAIL_TRIGGER_SX,
  HoverDetail,
  type HoverDetailTriggerProps,
} from '../work';

import type { HomeQueueRow } from './homeQueues';
import { issueSummary } from './issueSummary';

/**
 * The Issues cell of the Claims to review card (GDN-3379): the count, then
 * the blocker, on ONE line, with the whole story in a hover detail.
 *
 * One line because the card sits in a grid beside three others, and a
 * two-line cell made this card taller than all of them. The count leads and
 * the detail follows, the reverse of the Claims worklist: that column is
 * 1.8fr wide and can carry a whole sentence, this one cannot, and a
 * truncated sentence with the count hidden below it tells a biller scanning
 * five cards nothing at all. The sentence is not lost: it opens the popover.
 */
export interface ReviewIssueCellProps {
  item: HomeQueueRow;
  /**
   * The popover's content, handed the cell's own headline so the two agree.
   * Absent, the line stands alone. Rendered only while the popover is open.
   */
  renderDetail?: (item: HomeQueueRow, headline: string) => React.ReactNode;
  /** The popover opened, by hover or by focus. Once per open. */
  onDetailOpen?: (item: HomeQueueRow) => void;
}

export const ReviewIssueCell = ({ item, renderDetail, onDetailOpen }: ReviewIssueCellProps) => {
  const { blocking, detail, headline } = issueSummary(item);
  const content = renderDetail && hasIssueDetail(item) ? renderDetail(item, headline) : null;
  const line = (trigger?: HoverDetailTriggerProps) => (
    <Stack
      direction="row"
      spacing={0.75}
      {...trigger}
      sx={{ alignItems: 'center', minWidth: 0, ...(trigger ? HOVER_DETAIL_TRIGGER_SX : {}) }}
    >
      <ErrorOutlineIcon
        color={blocking ? 'error' : 'warning'}
        sx={{ flexShrink: 0, fontSize: 16 }}
      />
      {/* `component="div"`: `noWrap` sets text-overflow: ellipsis, which has
          no effect on an inline box, and the long blocker sentences would
          clip square at the cell edge instead of trailing off. */}
      <Typography
        component="div"
        data-testid={`home-issue-line-${item.claim_id}`}
        noWrap
        sx={{ fontSize: '0.78rem', minWidth: 0 }}
      >
        <Box component="span" sx={{ fontWeight: 600 }}>
          {headline}
        </Box>
        {detail && (
          <>
            <Box aria-hidden component="span" sx={{ color: 'text.secondary' }}>
              {' · '}
            </Box>
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {detail}
            </Box>
          </>
        )}
      </Typography>
    </Stack>
  );
  return content ? (
    <HoverDetail content={content} onOpen={() => onDetailOpen?.(item)}>
      {line}
    </HoverDetail>
  ) : (
    line()
  );
};
